import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { folderPath } from "../../../utils/folders";

// Shared look of the test builder (same tokens as the question bank pages).
export const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
export const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
export const iconButtonClass = `inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing}`;
export const tintButtonClass = `inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-lg bg-blue-50 px-2.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10 transition duration-200 enabled:hover:bg-blue-600 enabled:hover:text-white disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;
export const linkButtonClass = `inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 ${focusRing}`;
// A secondary action beside the one main button: icon and text, no frame (44px tall on phones).
export const quietButtonClass = `inline-flex h-11 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 ${focusRing}`;
// "← Back to …" above a page of the test bank (44px tall on phones).
export const backLinkClass = `inline-flex items-center gap-1.5 self-start rounded-lg px-1.5 py-1 text-sm font-medium text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-900 max-sm:min-h-11 ${focusRing}`;

const STATUS_STYLES = {
  APPROVED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  PENDING_REVIEW: "bg-amber-50 text-amber-800 ring-amber-600/20",
  UNDER_REVIEW: "bg-blue-50 text-blue-700 ring-blue-600/20",
  CHANGES_REQUESTED: "bg-violet-50 text-violet-700 ring-violet-600/20",
  RESUBMITTED: "bg-cyan-50 text-cyan-800 ring-cyan-600/20",
  DECLINED: "bg-red-50 text-red-700 ring-red-600/20",
  ARCHIVED: "bg-slate-100 text-slate-600 ring-slate-500/20",
  DRAFT: "bg-slate-100 text-slate-600 ring-slate-500/20",
};

export const statusLabel = (t, status) =>
  status?.code ? t(`test_bank.question_status.${status.code.toLowerCase()}`, { defaultValue: status.name }) : status?.name || "";

export function StatusBadge({ status }) {
  const [t] = useTranslation();
  if (!status) return null;
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        STATUS_STYLES[status.code] || STATUS_STYLES.DRAFT
      }`}
    >
      {statusLabel(t, status)}
    </span>
  );
}

export const isApproved = (question) => question?.status?.code === "APPROVED";
// A passage lists its authors' drafts too; a draft is not finished and never goes into a test.
export const isDraft = (question) => question?.status?.code === "DRAFT";

// The exam of the question's subject takes only approved questions into tests (folder.parent.
// approved_only_in_tests) and this one is not approved: it can't be added (the server answers 400
// not_approved), and once removed from a test it can't come back until it is approved. A passage's questions
// carry no folder: pass the passage's as `folder`.
export const isTestBlocked = (question, folder = question?.folder) =>
  !!folder?.parent?.approved_only_in_tests && question?.status?.code !== "APPROVED";

// A test's question whose answers the user may not read in the question bank (answers_hidden): its options come
// without the right one (is_correct null). Nothing is wrong with it: it is said quietly, and the test keeps it as any
// other. The bank's own rows never carry the flag.
export const answersHidden = (question) => question?.answers_hidden === true;

// "Exam · Subject · Topic › Subtopic" for a list or full question (the folder names the subject).
export const questionMeta = (question) =>
  [
    question?.folder ? folderPath(question.folder) : question?.subject?.name,
    [question?.subtopic?.topic?.name, question?.subtopic?.name].filter(Boolean).join(" › "),
  ]
    .filter(Boolean)
    .join(" · ");

export function Spinner({ className = "h-4 w-4" }) {
  return (
    <span
      className={`inline-block shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 ${className}`}
      aria-hidden="true"
    />
  );
}

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}
